import { useState } from "react";
import { b2bSignIn } from "@/api/b2bAuth";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./B2bLoginPage.module.css";

interface B2bLoginFormProps {
  usernameLabel: string;
  passwordLabel: string;
  /** Shown when the backend doesn't supply a localized error (e.g. network failure). */
  fallbackError: string;
  /** Informational message from a completed flow (e.g. password reset). */
  notice: string | null;
  onForgotPassword: () => void;
  onSuccess: () => void;
}

/** Bare "<status> <statusText>" errors from the Result layer carry no user-readable message. */
const HTTP_STATUS_ERROR_RE = /^\d{3}\s/;

export function B2bLoginForm({
  usernameLabel,
  passwordLabel,
  fallbackError,
  notice,
  onForgotPassword,
  onSuccess,
}: B2bLoginFormProps) {
  const texts = useLocalizedTexts();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    const result = await b2bSignIn(username, password);
    if (result.ok) {
      onSuccess();
      return;
    }

    setError(
      HTTP_STATUS_ERROR_RE.test(result.error) ? fallbackError : result.error
    );
    setIsSubmitting(false);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {notice && <p className={styles.success}>{notice}</p>}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="b2b-username">
          {usernameLabel}
        </label>
        <input
          id="b2b-username"
          className={styles.input}
          type="text"
          autoComplete="username"
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="b2b-password">
          {passwordLabel}
        </label>
        <input
          id="b2b-password"
          className={styles.input}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <button
        type="button"
        className={styles.linkButton}
        onClick={onForgotPassword}
      >
        {t(texts, "Tracker.Generic.ForgotPassword", "Forgot password?")}
      </button>
      <button
        type="submit"
        className={styles.submitButton}
        disabled={isSubmitting}
      >
        {t(texts, "Tracker.Generic.Login", "Log in")}
      </button>
    </form>
  );
}
